/**
 * @file Плавное отображение числа без изменения состояния самой передачи.
 *
 * requestAnimationFrame обновляет картинку между подтверждёнными значениями.
 * Новая задача сбрасывает анимацию; остановка и reduced motion показывают итог сразу.
 */
import { useEffect, useRef, useState } from 'react'

import { interpolateProgress } from './progress'

export function useSmoothNumber(
    target: number,
    identity: string,
    animate: boolean,
) {
    const [display, setDisplay] = useState({ identity, value: target })
    const current = useRef({ identity, value: target })

    // display нужен React для перерисовки, current — для продолжения анимации
    // с последнего кадра, когда новая цель придёт раньше завершения старой.
    useEffect(() => {
        // identity — идентификатор задачи/баланса. Новая задача не наследует старые цифры.
        const from =
            current.current.identity === identity
                ? current.current.value
                : target

        let frame = 0

        function update(value: number) {
            current.current = { identity, value }
            setDisplay({ identity, value })
        }

        // На завершённой задаче и при reduced motion промежуточные кадры не нужны.
        if (
            !animate ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            from === target
        ) {
            update(target)

            return
        }

        const start = performance.now()

        function step(now: number) {
            update(interpolateProgress(from, target, now - start))
            if (now - start < 180) frame = requestAnimationFrame(step)
        }

        // Это первый кадр; дальше step сам запрашивает следующий до истечения 180 мс.
        frame = requestAnimationFrame(step)

        // При новой цели продолжаем от последнего показанного числа, а старый кадр отменяем.
        return () => cancelAnimationFrame(frame)
    }, [target, identity, animate])

    // При смене задачи сразу отдаём её число: до следующего эффекта нельзя
    // показывать пользователю остаток анимации от предыдущего файла.
    return display.identity === identity && animate ? display.value : target
}
